import { useState } from 'react';
import { 
  FileText, 
  Download, 
  Search, 
  Filter,
  CheckCircle2,
  Star,
  Eye,
  Calendar,
  Building2,
  Zap,
  Shield
} from 'lucide-react';

interface Template {
  id: string;
  title: string;
  description: string;
  category: 'commercial' | 'residential' | 'industrial' | 'healthcare' | 'educational';
  fileType: 'DWG' | 'PDF' | 'XLSX' | 'DOCX';
  fileSize: string;
  downloads: number;
  rating: number;
  lastUpdated: string;
  nfpaCompliant: boolean;
  featured: boolean;
}

const templates: Template[] = [
  {
    id: '1',
    title: 'Commercial Office Building - Fire Alarm Layout',
    description: 'Complete fire alarm system design for 5-story commercial office building with addressable system, including device placement, riser diagrams, and specifications.',
    category: 'commercial',
    fileType: 'DWG',
    fileSize: '2.4 MB',
    downloads: 1247,
    rating: 4.8,
    lastUpdated: '2024-10-15',
    nfpaCompliant: true,
    featured: true
  },
  {
    id: '2',
    title: 'Retail Store Fire Alarm Plan',
    description: 'Single-story retail space fire alarm design with conventional system, notification appliances, and ADA compliance.',
    category: 'commercial',
    fileType: 'PDF',
    fileSize: '1.8 MB',
    downloads: 892,
    rating: 4.6,
    lastUpdated: '2024-09-28',
    nfpaCompliant: true,
    featured: false
  },
  {
    id: '3',
    title: 'Multi-Family Residential Complex',
    description: 'Fire alarm system for 3-building apartment complex with central monitoring, smoke detectors, and voice evacuation.',
    category: 'residential',
    fileType: 'DWG',
    fileSize: '3.1 MB',
    downloads: 654,
    rating: 4.7,
    lastUpdated: '2024-10-01',
    nfpaCompliant: true,
    featured: true
  },
  {
    id: '4',
    title: 'Industrial Warehouse Fire Protection',
    description: 'High-ceiling warehouse fire alarm design with beam detectors, manual pull stations, and mass notification.',
    category: 'industrial',
    fileType: 'DWG',
    fileSize: '2.9 MB',
    downloads: 543,
    rating: 4.9,
    lastUpdated: '2024-10-20',
    nfpaCompliant: true,
    featured: true
  },
  {
    id: '5',
    title: 'Healthcare Facility Fire Alarm System',
    description: 'Hospital wing fire alarm design with area of refuge, nurse call integration, and emergency voice communication.',
    category: 'healthcare',
    fileType: 'PDF',
    fileSize: '2.2 MB',
    downloads: 421,
    rating: 4.8,
    lastUpdated: '2024-09-15',
    nfpaCompliant: true,
    featured: false
  },
  {
    id: '6',
    title: 'Educational Campus Building',
    description: 'School building fire alarm with classroom coverage, corridor notification, and emergency lockdown integration.',
    category: 'educational',
    fileType: 'DWG',
    fileSize: '2.6 MB',
    downloads: 738,
    rating: 4.7,
    lastUpdated: '2024-10-10',
    nfpaCompliant: true,
    featured: false
  },
  {
    id: '7',
    title: 'Fire Alarm Riser Diagram Template',
    description: 'Standard riser diagram template with loop configurations, power calculations, and device addressing.',
    category: 'commercial',
    fileType: 'DWG',
    fileSize: '0.8 MB',
    downloads: 1523,
    rating: 4.9,
    lastUpdated: '2024-10-25',
    nfpaCompliant: true,
    featured: true
  },
  {
    id: '8',
    title: 'Fire Alarm Specifications Template',
    description: 'Comprehensive specification document template covering all NFPA 72 requirements and installation standards.',
    category: 'commercial',
    fileType: 'DOCX',
    fileSize: '0.3 MB',
    downloads: 2104,
    rating: 4.8,
    lastUpdated: '2024-10-18',
    nfpaCompliant: true,
    featured: true
  },
  {
    id: '9',
    title: 'Device Schedule & Calculations',
    description: 'Excel template for device schedules, power calculations, battery backup sizing, and voltage drop analysis.',
    category: 'commercial',
    fileType: 'XLSX',
    fileSize: '0.5 MB',
    downloads: 1876,
    rating: 4.9,
    lastUpdated: '2024-10-22',
    nfpaCompliant: true,
    featured: true
  }
];

const categories = [
  { value: 'all', label: 'All Categories', icon: Building2 },
  { value: 'commercial', label: 'Commercial', icon: Building2 },
  { value: 'residential', label: 'Residential', icon: Building2 },
  { value: 'industrial', label: 'Industrial', icon: Zap },
  { value: 'healthcare', label: 'Healthcare', icon: Shield },
  { value: 'educational', label: 'Educational', icon: Building2 }
];

export function DesignTemplatesPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    const matchesFeatured = !showFeaturedOnly || template.featured;
    
    return matchesSearch && matchesCategory && matchesFeatured;
  });

  const handleDownload = (template: Template) => {
    console.log('Downloading template:', template.title);
    // In production, trigger actual file download
    alert(`Downloading: ${template.title}\nFile Type: ${template.fileType}\nSize: ${template.fileSize}`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <FileText className="w-16 h-16 mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Design Templates</h1>
            <p className="text-xl text-purple-100">
              Professional fire alarm design templates, drawings, and specifications
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Search and Filter Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Search Templates
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by title or description..."
                  className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Filter className="inline w-4 h-4 mr-1" />
                Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Featured Filter */}
          <div className="mt-4 flex items-center">
            <input
              type="checkbox"
              id="featured"
              checked={showFeaturedOnly}
              onChange={(e) => setShowFeaturedOnly(e.target.checked)}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-slate-300 rounded"
            />
            <label htmlFor="featured" className="ml-2 text-sm text-slate-700">
              Show featured templates only
            </label>
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-slate-600">
            Showing {filteredTemplates.length} of {templates.length} templates
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map(template => (
            <div key={template.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              {/* Template Header */}
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded text-xs font-semibold text-white">
                        {template.fileType}
                      </span>
                      {template.featured && (
                        <Star className="w-4 h-4 text-yellow-300 ml-2 fill-current" />
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-white line-clamp-2">
                      {template.title}
                    </h3>
                  </div>
                </div>
              </div>

              {/* Template Body */}
              <div className="p-4">
                <p className="text-sm text-slate-600 mb-4 line-clamp-3">
                  {template.description}
                </p>

                {/* Template Stats */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="flex items-center text-xs text-slate-500">
                    <Download className="w-4 h-4 mr-1" />
                    {template.downloads.toLocaleString()} downloads
                  </div>
                  <div className="flex items-center text-xs text-slate-500">
                    <Eye className="w-4 h-4 mr-1" />
                    {template.fileSize}
                  </div>
                  <div className="flex items-center text-xs text-slate-500">
                    <Star className="w-4 h-4 mr-1 text-yellow-500" />
                    {template.rating} rating
                  </div>
                  <div className="flex items-center text-xs text-slate-500">
                    <Calendar className="w-4 h-4 mr-1" />
                    {new Date(template.lastUpdated).toLocaleDateString()}
                  </div>
                </div>

                {/* NFPA Badge */}
                {template.nfpaCompliant && (
                  <div className="flex items-center text-xs text-green-600 mb-4">
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    NFPA 72 Compliant
                  </div>
                )}

                {/* Category Badge */}
                <div className="mb-4">
                  <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full capitalize">
                    {template.category}
                  </span>
                </div>

                {/* Download Button */}
                <button
                  onClick={() => handleDownload(template)}
                  className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-lg transition-all duration-200 flex items-center justify-center"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download Template
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredTemplates.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No templates found</h3>
            <p className="text-slate-500">Try adjusting your search or filters</p>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-12 bg-gradient-to-br from-purple-600 to-pink-600 text-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold mb-4">About Our Templates</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold mb-2 flex items-center">
                <CheckCircle2 className="w-5 h-5 mr-2" />
                NFPA 72 Compliant
              </h3>
              <p className="text-purple-100 text-sm">
                All templates are designed to meet current NFPA 72 standards and local code requirements.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2 flex items-center">
                <CheckCircle2 className="w-5 h-5 mr-2" />
                Professional Quality
              </h3>
              <p className="text-purple-100 text-sm">
                Created by licensed fire alarm engineers with years of design experience.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2 flex items-center">
                <CheckCircle2 className="w-5 h-5 mr-2" />
                Fully Editable
              </h3>
              <p className="text-purple-100 text-sm">
                All templates are provided in editable formats (DWG, DOCX, XLSX) for customization.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2 flex items-center">
                <CheckCircle2 className="w-5 h-5 mr-2" />
                Regular Updates
              </h3>
              <p className="text-purple-100 text-sm">
                Templates are regularly updated to reflect code changes and industry best practices.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
